import { Fragment, useEffect } from "react";
import SiteLayout from "../../SiteLayout";
import { CHANGELOG } from "../../data/changelog";
import "../../styles/changelog.css";

// Renders a string with **bold** spans (kept tiny so changelog copy stays plain-text editable).
function Inline({ text }) {
  return text.split(/(\*\*[^*]+\*\*)/g).map((part, i) =>
    part.startsWith("**") && part.endsWith("**") ? (
      <strong key={i}>{part.slice(2, -2)}</strong>
    ) : (
      <Fragment key={i}>{part}</Fragment>
    )
  );
}

// Eager (not lazy) so the layout above a deep-link target is stable and #anchors land right.
function Shot({ src, alt }) {
  return (
    <div className="shot">
      <img src={src} alt={alt} />
    </div>
  );
}

function formatDate(iso) {
  return new Date(`${iso}T00:00:00`).toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

function slugify(text) {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, "")
    .trim()
    .replace(/\s+/g, "-");
}

// Entry id, e.g. "june-23-2026" — explicit slug, else derived from the date.
function entryAnchor(entry) {
  return entry.slug || slugify(formatDate(entry.date));
}

// A heading with a copyable "#" link — clicking sets the URL hash so it can be grabbed
// from the address bar and pasted into an email. The id lives on the wrapper element.
function Anchored({ href, level, className, children }) {
  const Tag = level;
  return (
    <Tag className={className}>
      <a className="anchor" href={href} aria-label="Link to this section">
        #
      </a>
      {children}
    </Tag>
  );
}

export default function SiteChangelog() {
  // Land on the right section when arriving from an email deep link (re-scroll a few
  // times as images settle, so the target stays put under the fixed nav).
  useEffect(() => {
    const id = window.location.hash.slice(1);
    if (!id) return;
    const timers = [0, 200, 600].map((ms) =>
      setTimeout(() => document.getElementById(id)?.scrollIntoView({ block: "start" }), ms)
    );
    return () => timers.forEach(clearTimeout);
  }, []);

  return (
    <SiteLayout
      page="changelog"
      title="Changelog | Shadow"
      navVariant="flat"
      navActive="changelog"
    >
      <header className="hero">
        <div className="wrap">
          <div className="eyebrow gray">Changelog</div>
          <h1>What's new in Shadow</h1>
          <p className="lede">
            Every meaningful update to Shadow, newest first — new features, improvements, and fixes.
          </p>
        </div>
      </header>

      <div className="wrap log">
        {CHANGELOG.map((entry) => {
          const entryId = entryAnchor(entry);
          return (
            <article key={entry.date} id={entryId} className="entry reveal">
              <div className="entry-meta">
                <time dateTime={entry.date}>{formatDate(entry.date)}</time>
                {entry.version && <span className="ver">{entry.version}</span>}
              </div>
              <div className="entry-body">
                <Anchored href={`#${entryId}`} level="h2" className="entry-title">
                  {entry.title}
                </Anchored>
                {entry.intro && (
                  <p className="entry-intro">
                    <Inline text={entry.intro} />
                  </p>
                )}

                {entry.sections.map((section, si) => {
                  const sectionId = `${entryId}-${section.slug || slugify(section.heading)}`;
                  return (
                    <section key={si} id={sectionId} className="sec">
                      <Anchored
                        href={`#${sectionId}`}
                        level="h3"
                        className={section.big ? "sec-head big" : "sec-head"}
                      >
                        {section.heading}
                      </Anchored>
                      {section.body && (
                        <p className="sec-body">
                          <Inline text={section.body} />
                        </p>
                      )}

                      {section.items?.map((item, ii) => (
                        <div key={ii} className="sub">
                          <h4>{item.title}</h4>
                          {item.body && (
                            <p>
                              <Inline text={item.body} />
                            </p>
                          )}
                          {item.image && <Shot {...item.image} />}
                        </div>
                      ))}

                      {section.images?.map((img, gi) => (
                        <Shot key={gi} {...img} />
                      ))}

                      {section.bullets && (
                        <ul className="bullets">
                          {section.bullets.map((b, bi) => (
                            <li key={bi}>
                              <Inline text={b} />
                            </li>
                          ))}
                        </ul>
                      )}
                    </section>
                  );
                })}
              </div>
            </article>
          );
        })}
      </div>
    </SiteLayout>
  );
}
